<template>
  <div class="wc-team-detail">
    <load-state
      :state="state"
      empty-text="球队信息不存在"
      empty-action-text="返回球队列表"
      @empty-action="backToTeams"
      @retry="load"
    >
      <div class="wc-team-hero">
        <button class="wc-change-team" @click="backToTeams">换支球队</button>
        <div class="wc-team-profile">
          <team-logo :src="team.team_logo" :name="team.team_name" />
          <div>
            <div class="wc-team-profile__name">{{ team.team_name }}</div>
            <div class="wc-team-profile__group">{{ team.team_group }} 组</div>
          </div>
        </div>
      </div>
      <div class="wc-detail-content">
        <div class="wc-section">
          <div class="wc-section__title">
            <span class="wc-section__icon wc-section__icon--rank"></span>
            <span>{{ team.team_group }} 组积分榜</span>
          </div>
          <rank-table :teams="groupTeams" :current-team-id="teamId" />
        </div>
        <div class="wc-section wc-section--schedule">
          <div class="wc-section__title">
            <span class="wc-section__icon wc-section__icon--schedule"></span>
            <span>全部赛程</span>
          </div>
          <load-state :state="scheduleState" empty-text="暂无赛程" @retry="loadSchedule(false)">
            <div class="wc-match-list">
              <div v-for="day in scheduleDays" :key="day.schedule_date" class="wc-date-block">
                <div class="wc-date-block__title">
                  <span>{{ dateMain(day) }}</span>
                  <span class="wc-date-block__week">{{ dateWeek(day) }}</span>
                </div>
                <match-card
                  v-for="(match, matchIndex) in day.schedule_list"
                  :key="matchKey(match, matchIndex)"
                  :match="match"
                  compact-type
                  :highlight-team-id="teamId"
                />
              </div>
            </div>
          </load-state>
        </div>
      </div>
    </load-state>
  </div>
</template>

<script>
import { getRank, getSchedule } from '@/api/worldcup2026'
import LoadState from '../components/LoadState.vue'
import TeamLogo from '../components/TeamLogo.vue'
import RankTable from '../components/RankTable.vue'
import MatchCard from '../components/MatchCard.vue'
import { worldcupCache } from '../utils/cache'
import { formatDateTitle } from '../utils/time'
import { getMatchListKey } from '../utils/match'

export default {
  name: 'WorldCupTeamDetail',
  components: { LoadState, TeamLogo, RankTable, MatchCard },
  data () {
    return {
      state: 'idle',
      scheduleState: 'idle',
      team: {},
      groupTeams: [],
      scheduleDays: []
    }
  },
  computed: {
    teamId () {
      return this.$route.params.teamId
    }
  },
  created () {
    this.load()
  },
  methods: {
    backToTeams () {
      const query = this.$route.query.screen ? { screen: this.$route.query.screen } : {}
      this.$router.push({ path: '/worldcup2026/team', query })
    },
    dateTitle (day) {
      return formatDateTitle(day)
    },
    dateMain (day) {
      return this.dateTitle(day).split(/\s+/)[0] || ''
    },
    dateWeek (day) {
      return this.dateTitle(day).split(/\s+/).slice(1).join(' ')
    },
    matchKey (match, index) {
      return getMatchListKey(match, index)
    },
    async load () {
      this.state = 'loading'
      try {
        let groups = worldcupCache.rank
        if (!groups) {
          const resp = await getRank()
          if (resp.error_code !== 0) {
            this.state = 'error'
            return
          }
          groups = resp.result && resp.result.data ? resp.result.data : []
          worldcupCache.rank = groups
        }
        this.findTeam(groups)
        if (!this.team.id) {
          this.state = 'empty'
          return
        }
        this.state = 'success'
        this.loadSchedule(true)
      } catch (e) {
        this.state = 'error'
      }
    },
    findTeam (groups) {
      this.team = {}
      this.groupTeams = []
      groups.forEach(group => {
        const teams = [...(group.team_rank || [])].sort((a, b) => Number(a.rank || 0) - Number(b.rank || 0))
        const found = teams.find(team => String(team.id) === String(this.teamId))
        if (found) {
          this.team = found
          this.groupTeams = teams
        }
      })
    },
    async loadSchedule (useCache) {
      if (useCache && worldcupCache.teamSchedules[this.teamId]) {
        this.applySchedule(worldcupCache.teamSchedules[this.teamId])
        return
      }
      this.scheduleState = 'loading'
      try {
        const resp = await getSchedule({ team_id: this.teamId })
        if (resp.error_code !== 0) {
          this.scheduleState = 'error'
          return
        }
        const days = resp.result && resp.result.data ? resp.result.data : []
        worldcupCache.teamSchedules[this.teamId] = days
        this.applySchedule(days)
      } catch (e) {
        this.scheduleState = 'error'
      }
    },
    applySchedule (days) {
      this.scheduleDays = days
      this.scheduleState = days.length ? 'success' : 'empty'
    }
  }
}
</script>

<style scoped lang="scss">
.wc-team-detail {
  padding-bottom: 16px;
}

.wc-team-detail ::v-deep .wc-team-hero {
  padding: 12px 16px 16px;
  background: #ffffff;
}

.wc-change-team {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  color: #b8880a;
  background: transparent;
  font-size: 13px;
  line-height: 19px;
}

.wc-change-team::before {
  content: '';
  width: 7px;
  height: 7px;
  border-left: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg);
}

.wc-detail-content {
  padding: 12px;
}

.wc-team-profile,
.wc-section {
  background: #ffffff;
}

.wc-team-profile {
  display: flex;
  align-items: center;
  margin-top: 14px;
}

.wc-team-profile ::v-deep .wc-team-logo {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border-radius: 0;
  background: #ffffff;
  line-height: 56px;
  font-size: 14px;
}

.wc-team-profile ::v-deep .wc-team-logo img {
  box-sizing: border-box;
  padding: 4px;
  object-fit: contain;
}

.wc-team-profile__name {
  margin-left: 14px;
  color: #1a1a1a;
  font-size: 20px;
  line-height: 24px;
  font-weight: 800;
}

.wc-team-profile__group {
  display: inline-block;
  margin: 5px 0 0 14px;
  padding: 2px 8px;
  border-radius: 4px;
  color: #b8880a;
  background: rgba(200, 162, 40, 0.12);
  font-size: 12px;
  font-weight: 600;
}

.wc-section {
  margin-top: 12px;
  border: 1px solid #dce1eb;
  border-radius: 12px;
  overflow: hidden;
}

.wc-section:first-child {
  margin-top: 0;
}

.wc-section__title {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #edf0f3;
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 700;
}

.wc-section__icon {
  position: relative;
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  color: #b8880a;
}

.wc-section__icon--rank::before {
  content: '';
  position: absolute;
  inset: 2px 3px 4px;
  border: 1.5px solid currentColor;
  border-top-width: 3px;
  border-radius: 2px 2px 4px 4px;
}

.wc-section__icon--rank::after {
  content: '';
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 1px;
  height: 2px;
  background: currentColor;
  border-radius: 2px;
}

.wc-section__icon--schedule::before {
  content: '';
  position: absolute;
  inset: 2px 1px 1px;
  border: 1.5px solid currentColor;
  border-radius: 2px;
}

.wc-section__icon--schedule::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 0;
  width: 6px;
  height: 5px;
  border-left: 1.5px solid currentColor;
  border-right: 1.5px solid currentColor;
}

.wc-match-list {
  padding: 4px 0;
}

.wc-date-block {
  border-top: 1px solid #edf0f3;
}

.wc-date-block:first-child {
  border-top: 0;
}

.wc-date-block__title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 6px;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
}

.wc-date-block__week {
  color: #8a96a8;
  font-size: 12px;
  font-weight: 400;
}

.wc-section--schedule ::v-deep .wc-match-card {
  grid-template-columns: 44px 1fr 54px;
  gap: 10px;
  align-items: center;
  min-height: 78px;
  margin: 0;
  padding: 0 14px 10px;
  border-radius: 0;
  background: transparent;
}

.wc-section--schedule ::v-deep .wc-match-card__side {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.wc-section--schedule ::v-deep .wc-match-card__time {
  color: #4a5568;
  font-size: 12px;
  line-height: 12px;
  font-weight: 600;
}

.wc-section--schedule ::v-deep .wc-match-card__type {
  margin-top: 4px;
  color: #8a96a8;
  font-size: 10px;
  line-height: 10px;
}

.wc-section--schedule ::v-deep .wc-match-card__teams {
  gap: 5px;
}

.wc-section--schedule ::v-deep .wc-match-card__team {
  height: 24px;
}

.wc-section--schedule ::v-deep .wc-match-card__team .wc-team-logo {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border-radius: 0;
  background: transparent;
  line-height: 20px;
  font-size: 10px;
}

.wc-section--schedule ::v-deep .wc-match-card__team .wc-team-logo img {
  object-fit: contain;
}

.wc-section--schedule ::v-deep .wc-match-card__name {
  margin-left: 6px;
  font-size: 13px;
}

.wc-section--schedule ::v-deep .wc-match-card__score {
  min-width: 16px;
  margin-left: 6px;
  font-size: 13px;
  text-align: right;
}

.wc-section--schedule ::v-deep .wc-match-card__status {
  color: #8a96a8;
  font-size: 12px;
  text-align: right;
}
</style>
